import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { profileAPI, cancelProactiveRefresh, scheduleProactiveRefresh } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { CompanyPicker } from './ui';

const AUTH_PAGES = ['/login', '/signup', '/reset-password', '/reset-password-verify', '/mfa-verify', '/verify-email'];

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setThemeFromProfile } = useTheme();
  const { user, loading } = useAuth();
  const isAuthenticated = !!user;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userProfile, setUserProfile] = useState({ permission_level: 'user' });
  const dropdownRef = useRef(null);
  
  // Company switching state
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [switchingCompany, setSwitchingCompany] = useState(false);
  const [switchError, setSwitchError] = useState('');
  const [impersonationTick, setImpersonationTick] = useState(0);

  useEffect(() => {
    const handleImpersonationChange = () => setImpersonationTick(t => t + 1);
    window.addEventListener('impersonation-changed', handleImpersonationChange);
    window.addEventListener('storage', (e) => { if (e.key === 'impersonation') setImpersonationTick(t => t + 1); });
    return () => {
      window.removeEventListener('impersonation-changed', handleImpersonationChange);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // Load full profile in background
      loadUserProfile();
      // Load user's companies
      loadUserCompanies();
    } else {
      // Reset profile when logged out
      setUserProfile({
        permission_level: 'user'
      });
      setCompanies([]);
      setCurrentCompany(null);
    }

    // Listen for profile updates (e.g., profile picture changes)
    const handleProfileUpdate = () => {
      if (isAuthenticated) {
        loadUserProfile();
      }
    };
    window.addEventListener('profileUpdated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, [isAuthenticated]);

  const loadUserCompanies = async () => {
    try {
      const response = await fetch('/api/auth/my-companies', { credentials: 'include' });
      const data = await response.json();
      if (data.companies) {
        setCompanies(data.companies);
        const current = data.companies.find(c => c.is_current);
        setCurrentCompany(current || null);
      }
    } catch (error) {
      console.error('Failed to load user companies:', error);
    }
  };

  const handleSwitchCompany = async (companyId) => {
    console.log('🔄 handleSwitchCompany called with companyId:', companyId);
    const targetCompanyId = companyId || selectedCompanyId;
    
    if (!targetCompanyId) {
      console.error('❌ No company ID provided');
      setSwitchError('No company selected');
      return;
    }
    
    setSwitchingCompany(true);
    setSwitchError('');
    
    try {
      console.log('🔑 Switching to company ID:', targetCompanyId);
      
      const response = await fetch('/api/auth/switch-company', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ company_id: targetCompanyId })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        scheduleProactiveRefresh();
        setShowCompanyModal(false);
        console.log('✅ Company switched successfully, reloading page...');
        // Reload the page to refresh all data with new company context
        window.location.reload();
      } else {
        const errorMsg = data.error || data.message || 'Failed to switch company';
        console.error('❌ Switch failed:', errorMsg);
        setSwitchError(errorMsg);
      }
    } catch (error) {
      console.error('❌ Failed to switch company:', error);
      setSwitchError('Network error: ' + error.message);
    } finally {
      setSwitchingCompany(false);
    }
  };

  const loadUserProfile = async () => {
    try {
      const response = await profileAPI.getProfile();
      if (response.status === 'success' && response.user) {
        setUserProfile(response.user);
        // Sync theme from server
        if (response.user.theme_preference) {
          setThemeFromProfile(response.user.theme_preference);
        }
        // Apply primary color from server
        if (response.user.primary_color) {
          document.documentElement.style.setProperty('--color-primary', response.user.primary_color);
        } else {
          document.documentElement.style.setProperty('--color-primary', '#4F46E5');
        }
        // Apply secondary color from server
        if (response.user.secondary_color) {
          document.documentElement.style.setProperty('--color-secondary', response.user.secondary_color);
        } else {
          document.documentElement.style.setProperty('--color-secondary', '#7C3AED');
        }
        // Apply dark mode from server
        if (response.user.dark_mode) {
          document.documentElement.setAttribute('data-mode', 'dark');
        } else {
          document.documentElement.removeAttribute('data-mode');
        }
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    // Call logout endpoint — cookies are sent automatically, backend clears them
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      // Ignore — redirect is sufficient fallback
    }

    cancelProactiveRefresh();
    sessionStorage.removeItem('auto_redirected');
    window.location.href = '/login';
  };

  // Hide NavBar on authentication pages (login has its own branding)
  if (AUTH_PAGES.some(p => location.pathname.startsWith(p))) {
    return null;
  }

  const isImpersonating = !!(user && user.impersonating);
  // impersonationTick forces re-render when impersonation starts/stops
  void impersonationTick;

  return (
    <>
    <nav 
      className="fixed left-0 right-0 z-50 shadow-lg h-16 flex items-center transition-all duration-300" 
      style={{ 
        background: `linear-gradient(135deg, var(--color-primary, #4F46E5) 0%, var(--color-secondary, #7C3AED) 100%)`,
        top: isImpersonating ? '40px' : '0' 
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex items-center justify-between">
          {/* Left side: Logo + nav links + Manage dropdown */}
          <div className="flex-shrink-0 flex items-center gap-8 sm:gap-10">
            <Link to="/" className="text-white font-bold text-xl transition-opacity hover:opacity-80">
              TIMG Central
            </Link>
            {isAuthenticated && (
              <div className="flex items-center gap-6 sm:gap-8">
                <Link to="/" className="flex items-center space-x-1.5 text-white text-sm hover:opacity-80 transition-opacity">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span>My Apps</span>
                </Link>
                {userProfile?.permission_level === 'global_admin' || userProfile?.can_manage_users ? (
                  <Link to="/tasks" className="flex items-center space-x-1.5 text-white text-sm hover:opacity-80 transition-opacity">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <span>Tasks</span>
                  </Link>
                ) : null}
                {userProfile?.permission_level === 'global_admin' && (
                  <Link to="/management" className="flex items-center space-x-1.5 text-white text-sm hover:opacity-80 transition-opacity">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span>Management</span>
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Right side: Company switcher + Avatar dropdown */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                {/* Company context indicator */}
                {companies.length > 0 && (
                  <button
                    onClick={() => {
                      if (companies.length > 1) {
                        setSelectedCompanyId(currentCompany?.id || companies[0]?.id);
                        setShowCompanyModal(true);
                      }
                    }}
                    className={`flex items-center space-x-1 text-white text-sm transition-opacity focus:outline-none ${companies.length > 1 ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
                  >
                    <span>{currentCompany?.name || 'Select Company'}</span>
                    {companies.length > 1 && (
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </button>
                )}

                {/* Avatar dropdown — personal only */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center space-x-2 text-white focus:outline-none hover:opacity-90 transition-opacity rounded-full px-1 py-1"
                  >
                    {userProfile?.profile_picture_url ? (
                      <img
                        src={userProfile.profile_picture_url}
                        alt=""
                        className="w-12 h-12 rounded-full object-cover border-2 border-white"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center border-2 border-white" style={{ display: userProfile?.profile_picture_url ? 'none' : 'flex' }}>
                      <span className="text-sm font-medium">
                        {((userProfile?.firstname?.[0] || '') + (userProfile?.lastname?.[0] || '')).toUpperCase() ||
                         (user?.email?.[0] || 'U').toUpperCase()}
                      </span>
                    </div>
                    <svg className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Account
                      </Link>

                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={() => {
                            handleLogout();
                            setIsDropdownOpen(false);
                          }}
                          className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <svg className="w-4 h-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Log Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-white hover:text-gray-200"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="text-white hover:text-gray-200"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>

      {/* Switch Company Modal */}
      {showCompanyModal && (
        <CompanyPicker
          companies={companies}
          selectedId={currentCompany?.id}
          onSelect={handleSwitchCompany}
          onSetPrimary={async (companyId) => {
            await profileAPI.setPrimaryCompany(companyId);
            setCompanies(companies.map(c => ({ ...c, is_primary: c.id === companyId })));
          }}
          onCancel={() => {
            setShowCompanyModal(false);
            setSwitchError('');
          }}
          title="Switch Company"
          submitLabel="Switch"
          loading={switchingCompany}
          error={switchError}
        />
      )}
    </>
  );
};

export default NavBar;